import { useState, useEffect, useRef, useMemo } from "react";
import { TYPO } from "styles/Typo";
import { useRouter } from "next/router";
import List from "components/common/List/list";
import { calculrateReturnByLogReturn, sliceData } from "./PerformanceCharts";
import { useStrategyAssets } from "utils/clientAPIs/stragey";
import { STRATEGY_TYPE } from "utils/constant/STATUS";
import {
  ReturnListSkeleton,
  ReturnSectionSkeleton,
} from "components/skeleton/strategy";
import { useSession } from "next-auth/react";
import { Forward, ICONS } from "components/common/Icons";
import { MONTH_ENG } from "utils/constant/ETC";
import { useTranslation } from "next-i18next";
import StrategyAlarmRegister from "./AlarmRegister";
import OperationPeriodCards from "./OperationPeriodCards";
import StockDetailLink from "components/common/StockDetailLink";
import { getStockLogo } from "utils/image";
import { trackPageAction } from "lib/analytics/core/actions";

function StrategyReturns({
  logMock,
  kospiLogReturns,
  nasdaqLogReturns,
  sCode,
  standardBaseDtList,
  init,
  setInit,
  realtimeData,
}: {
  logMock: any;
  kospiLogReturns: any;
  nasdaqLogReturns: any;
  sCode: STRATEGY_TYPE;
  standardBaseDtList: any;
  init: boolean;
  setInit: (init: boolean) => void;
  realtimeData: any;
}) {
  const { t } = useTranslation("strategy");
  const { data: session, update }: any = useSession();
  const router = useRouter();

  const [date, setDate] = useState({
    startDate: standardBaseDtList?.[standardBaseDtList?.length - 1],
    endDate: undefined,
  });
  const [currentIndex, setCurrentMonth] = useState(
    standardBaseDtList.length - 1
  );
  const [assets, isValid] = useStrategyAssets(
    sCode,
    logMock?.[logMock?.length - 1]?.BASE_DT,
    date?.startDate,
    date?.endDate,
    currentIndex === standardBaseDtList.length - 1
  );

  const findRealEndDate = (endDate: any) => {
    if (!endDate) return undefined;
    const index = logMock.findIndex(
      (item: any) =>
        String(item.BASE_DT).split("T")[0] === String(endDate).split("T")[0]
    );
    if (index === -1) {
      return logMock[logMock?.length - 1]?.BASE_DT;
    }
    return logMock[index - 1]?.BASE_DT;
  };

  // periodReturn, periodKospiReturn에 실시간 데이터 추가
  const realTimeLogMean = realtimeData?.assets?.length
    ? realtimeData.assets.reduce(
        (acc: number, curr: any) => acc + curr?.PRICE_CHG_LOG,
        0
      ) / realtimeData?.assets?.length
    : null;

  const realtimeKospiLog = realtimeData?.kospi
    ? realtimeData.kospi?.PRICE_CHG_LOG
    : null;
  const realTimeLog = realtimeData?.assets?.length
    ? [
        ...logMock,
        {
          DAILY_CHG_LOG: realTimeLogMean,
          BASE_DT: new Date().toISOString().split("T")[0],
        },
      ]?.filter((log: any) => log?.DAILY_CHG_LOG !== null)
    : logMock;
  const realTimeKospiLog = realtimeData?.kospi?.length
    ? [
        ...kospiLogReturns,
        {
          DAILY_CHG_LOG: realtimeKospiLog,
          BASE_DT: new Date().toISOString().split("T")[0],
        },
      ]?.filter((log: any) => log?.DAILY_CHG_LOG !== null)
    : kospiLogReturns;

  function slicePeriod(
    day1: any,
    day2?: any,
    isKospi?: boolean,
    isXaxis?: boolean
  ) {
    const lastMonth = currentIndex === standardBaseDtList.length - 1;
    const realTimeData = lastMonth ? realtimeData : undefined;
    const data =
      lastMonth && realTimeData
        ? isKospi
          ? realTimeKospiLog
          : realTimeLog
        : isKospi
        ? kospiLogReturns
        : logMock;

    const day1Index = data.findIndex(
      (item: any) =>
        String(item.BASE_DT).split("T")[0] == String(day1).split("T")[0]
    );

    if (day1Index === -1) return [];
    const day2Index = day2
      ? data.findIndex(
          (item: any) =>
            String(item.BASE_DT).split("T")[0] === String(day2).split("T")[0]
        )
      : undefined;
    if (day2Index === undefined)
      return [
        ...data
          .slice(day1Index)
          .map((item: any) => (isXaxis ? item?.BASE_DT : item?.DAILY_CHG_LOG)),
      ];
    if (day2Index === -1) return [];
    return [
      ...data.slice(day1Index, day2Index + 1).map((item: any) => {
        return isXaxis ? item?.BASE_DT : item?.DAILY_CHG_LOG;
      }),
    ];
  }

  // 기간별 수익률 데이터
  const periodReturn = !isValid
    ? calculrateReturnByLogReturn(
        slicePeriod(assets?.startDate, assets?.endDate)
      )
    : undefined;
  const periodKospiReturn = !isValid
    ? calculrateReturnByLogReturn(
        slicePeriod(assets?.startDate, assets?.endDate, true)
      )
    : undefined;

  const moreThanKospi =
    periodReturn && periodKospiReturn
      ? periodReturn - periodKospiReturn
      : undefined;

  // 로그인 유저 여부
  const isLoginUser = session?.user?.email || false;
  useEffect(() => {
    setTimeout(() => {
      if (session?.user) {
        update();
      }
    }, 400);
  }, []);
  return (
    <div className="flex flex-col">
      {isValid && (
        <div className="flex flex-col gap-3">
          <ReturnSectionSkeleton />
          <div className={``}>
            <ReturnListSkeleton
              count={sCode === "rpn" ? 7 : sCode === "rs3" ? 3 : 7}
            />
          </div>
        </div>
      )}
      {!isValid && (
        <>
          <StrategyAlarmRegister sCode={sCode} init={init} setInit={setInit} />
          <div className="px-6 pt-[30px] flex flex-col gap-5 ">
            <span
              className={`${
                assets?.date ? "" : "hidden"
              } h-[38.5px] st-spec-date ${
                TYPO.TYPO7_MD
              } text-gray50 px-[10px] py-2 bg-gray5 rounded-[10px] w-fit`}
            >
              {router.locale === "ko" ? assets?.date : assets?.dateEng}
            </span>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div
                  className={`st-spec-prev-btn cursor-pointer
                    ${currentIndex === 0 ? "rotate-180" : ""}
                `}
                  onClick={() => {
                    trackPageAction("move_strat_round");

                    if (isLoginUser) {
                      window.dataLayer.push({
                        event: "cStratHistoryMember",
                      });
                    } else {
                      window.dataLayer.push({
                        event: "cStratHistoryNon",
                      });
                    }
                    if (currentIndex === 0) return;
                    const endDate = findRealEndDate(
                      standardBaseDtList[currentIndex]
                    );

                    setCurrentMonth(currentIndex - 1);

                    setDate({
                      startDate: standardBaseDtList[currentIndex - 1],
                      endDate: endDate,
                    });
                  }}
                >
                  {currentIndex === 0 ? <InactiveButton /> : <ActiveButton />}
                </div>
                <span
                  className={`${TYPO.TYPO5_1_SB} text-gray80 ${
                    router.locale === "ko" ? " w-[85px]" : " w-[100px]"
                  } whitespace-nowrap text-center`}
                >
                  {router.locale === "ko"
                    ? `${
                        Number(
                          (date.startDate as string).split("t")[0].split("-")[1]
                        ) || undefined
                      }월 수익률`
                    : `${
                        MONTH_ENG[
                          Number(
                            (date.startDate as string)
                              .split("t")[0]
                              .split("-")[1]
                          ) - 1
                        ] || undefined
                      }. Return`}
                </span>
                <div
                  className={`st-spec-next-btn ${
                    currentIndex === standardBaseDtList.length - 1
                      ? ""
                      : "rotate-180"
                  } cursor-pointer`}
                  onClick={() => {
                    trackPageAction("move_strat_round");
                    if (currentIndex + 1 === standardBaseDtList.length) return;
                    setCurrentMonth(currentIndex + 1);
                    setDate({
                      startDate: standardBaseDtList[currentIndex + 1],
                      endDate:
                        currentIndex + 2 > standardBaseDtList?.length - 1
                          ? undefined
                          : findRealEndDate(
                              standardBaseDtList[currentIndex + 2]
                            ),
                    });
                  }}
                >
                  {currentIndex === standardBaseDtList.length - 1 ? (
                    <InactiveButton />
                  ) : (
                    <ActiveButton />
                  )}
                </div>
              </div>
            </div>
          </div>
          {currentIndex === standardBaseDtList.length - 1 ? (
            <OperationPeriodCards
              periodReturn={periodReturn}
              logData={slicePeriod(assets?.startDate, assets?.endDate)}
              xaxisData={slicePeriod(
                assets?.startDate,
                assets?.endDate,
                false,
                true
              )}
              startDate={date?.startDate as string}
              month={assets?.month as string}
              items={assets?.list}
              lastDay={logMock?.[logMock?.length - 1]?.BASE_DT}
            />
          ) : (
            <div className="px-6 py-2 justify-between items-center flex">
              <div className="flex flex-col justify-center  gap-2 py-2">
                <span
                  className={`st-spec-return ${TYPO.TYPO1_MD} ${
                    periodReturn
                      ? Number(periodReturn.toFixed(1)) === 0
                        ? "text-gray50"
                        : periodReturn > 0
                        ? "text-red40"
                        : "text-blue40"
                      : "text-gray50"
                  }`}
                >
                  {periodReturn
                    ? Number(periodReturn.toFixed(1)) === 0
                      ? ""
                      : periodReturn > 0
                      ? "+"
                      : ""
                    : ""}

                  {periodReturn
                    ? Number(periodReturn.toFixed(1)) === 0
                      ? "0%"
                      : `${periodReturn.toFixed(1)}%`
                    : "0%"}
                </span>
                <div className={`flex items-center ${TYPO.TYPO7_MD} gap-1`}>
                  {router.locale === "ko" && (
                    <span className={` text-gray40`}>{`KOSPI보다`}</span>
                  )}
                  <span
                    className={`st-spec-kospi-return  ${
                      moreThanKospi
                        ? Number(moreThanKospi.toFixed(1)) === 0
                          ? "text-gray50"
                          : moreThanKospi > 0
                          ? "text-red40"
                          : "text-blue40"
                        : "text-gray50"
                    }`}
                  >
                    {moreThanKospi
                      ? Number(moreThanKospi.toFixed(1)) === 0
                        ? ""
                        : moreThanKospi > 0
                        ? "+"
                        : ""
                      : ""}
                    {moreThanKospi ? `${moreThanKospi.toFixed(1)}%` : "0%"}
                  </span>
                  {router.locale === "en" && (
                    <span className={` text-gray40`}>{`than KOSPI`}</span>
                  )}
                </div>
              </div>
              {currentIndex === standardBaseDtList.length - 1 && (
                <div className="flex gap-1 items-center">
                  <CurrentIcon />
                  <span className={`${TYPO.TYPO7_MD} text-gray40`}>
                    {t("inOperation")}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="py-2 flex flex-col gap-2 relative">
            {currentIndex === standardBaseDtList.length - 1 && (
              <div className="flex gap-1 items-center justify-end px-6">
                <span className={`${TYPO.TYPO8_RG} text-gray50`}>
                  {t("이번달")}
                </span>
              </div>
            )}
            {assets?.list?.length
              ? [...assets?.list]?.map((asset: any, index: number) => (
                  <StockDetailLink
                    ticker={asset.ITEM_CD_DL}
                    dataGtmId={`strategy-returns-list`}
                    key={index}
                  >
                    <div
                      key={index}
                      className={`flex px-1 justify-between hover:bg-gray2  item-cd-${asset.ITEM_CD} item-cddl-${asset.ITEM_CD_DL}`}
                    >
                      <List
                        className="hover:bg-gray2"
                        totalscore={
                          currentIndex === standardBaseDtList.length - 1
                            ? asset?.TOTAL_PER
                            : undefined
                        }
                        src={getStockLogo(asset.ITEM_CD_DL)}
                        item={
                          router.locale === "ko"
                            ? asset.ITEM_KR_NM && asset.ITEM_KR_NM
                            : asset.ITEM_ENG_NM && asset.ITEM_ENG_NM
                        }
                        HR_ITEM_NM={""}
                        rightIcon={() => {
                          return (
                            <div className="flex gap-3 items-center">
                              <span
                                className={`st-spec-asset-return ${
                                  TYPO.TYPO5_1_MD
                                } ${
                                  asset?.ADJ_CLOSE_CHG > 0
                                    ? "text-red40"
                                    : !asset?.ADJ_CLOSE_CHG ||
                                      asset?.ADJ_CLOSE_CHG === 0
                                    ? "text-gray50"
                                    : "text-blue40"
                                }`}
                              >
                                {asset?.ADJ_CLOSE_CHG > 0 ? "+" : ""}
                                {asset?.ADJ_CLOSE_CHG
                                  ? asset?.ADJ_CLOSE_CHG?.toFixed(1)
                                  : 0}
                                %
                              </span>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </StockDetailLink>
                ))
              : null}

            {currentIndex === standardBaseDtList.length - 1 &&
              assets?.notionLink && (
                <div
                  className={`py-2 px-6 flex items-center gap-3 cursor-pointer`}
                  onClick={() => {
                    window.dataLayer.push({
                      event: "cStratReport",
                    });
                    window.open(assets?.notionLink, "_blank");
                  }}
                >
                  {isLoginUser ? (
                    <>
                      <ReasonIcon />
                      <div className="flex flex-col grow">
                        <span className={`${TYPO.TYPO8_RG} text-gray60`}>
                          {router.locale === "ko"
                            ? Number(
                                (date.startDate as string)
                                  .split("t")[0]
                                  .split("-")[1]
                              ) || undefined
                            : MONTH_ENG[
                                Number(
                                  (date.startDate as string)
                                    .split("t")[0]
                                    .split("-")[1]
                                ) - 1
                              ]}
                          {router.locale === "ko"
                            ? "월 전략 구성"
                            : ". Holdings in strategy"}
                        </span>
                        <span className={`${TYPO.TYPO5_1_MD} text-gray80`}>
                          {t("whyAISelectedTheseStocks")}
                        </span>
                      </div>
                      <Forward height={16} width={16} fill={"gray30"} />
                    </>
                  ) : (
                    <div className="h-[62px] w-full"></div>
                  )}
                </div>
              )}
          </div>
        </>
      )}

      {/* <CustomToast
        message={
          router.locale === "ko"
            ? "페이지로 이동할 수 없습니다"
            : "Unable to go to page"
        }
        isSuccess={false}
        isVisible={errorToastVisible}
        onClose={() => setErrorToastVisible(false)}
        width={240}
      /> */}
    </div>
  );
}

const ActiveButton = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <mask
        id="mask0_15887_39254"
        // style="mask-type:alpha"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="24"
        height="24"
      >
        <rect width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_15887_39254)">
        <path
          d="M13.4767 16.8256L9.26163 12.6105C9.16473 12.5136 9.0969 12.4167 9.05814 12.3198C9.01938 12.2229 9 12.1163 9 12C9 11.8837 9.01938 11.7771 9.05814 11.6802C9.0969 11.5833 9.16473 11.4864 9.26163 11.3895L13.4767 7.17442C13.5349 7.11628 13.5979 7.07267 13.6657 7.0436C13.7335 7.01453 13.8062 7 13.8837 7C14.0388 7 14.1744 7.05329 14.2907 7.15988C14.407 7.26647 14.4651 7.40698 14.4651 7.5814V16.4186C14.4651 16.593 14.407 16.7335 14.2907 16.8401C14.1744 16.9467 14.0388 17 13.8837 17C13.845 17 13.7093 16.9419 13.4767 16.8256Z"
          fill="var(--gray70)"
        />
      </g>
    </svg>
  );
};

const InactiveButton = (fill: any) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <mask
        id="mask0_15887_39258"
        // style="mask-type:alpha"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="24"
        height="24"
      >
        <rect width="24" height="24" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_15887_39258)">
        <path
          d="M9.5814 17C9.42636 17 9.2907 16.9467 9.17442 16.8401C9.05814 16.7335 9 16.593 9 16.4186V7.5814C9 7.40698 9.05814 7.26647 9.17442 7.15988C9.2907 7.05329 9.42636 7 9.5814 7C9.62016 7 9.75581 7.05814 9.98837 7.17442L14.2035 11.3895C14.3004 11.4864 14.3682 11.5833 14.407 11.6802C14.4457 11.7771 14.4651 11.8837 14.4651 12C14.4651 12.1163 14.4457 12.2229 14.407 12.3198C14.3682 12.4167 14.3004 12.5136 14.2035 12.6105L9.98837 16.8256C9.93023 16.8837 9.86725 16.9273 9.79942 16.9564C9.73159 16.9855 9.65891 17 9.5814 17Z"
          fill="var(--gray30)"
        />
      </g>
    </svg>
  );
};

const CurrentIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M5.00065 10.0417C5.00065 10.2639 5.01454 10.4826 5.04232 10.6979C5.0701 10.9132 5.11871 11.125 5.18815 11.3333C5.2576 11.5694 5.25065 11.7951 5.16732 12.0104C5.08398 12.2257 4.93815 12.3819 4.72982 12.4792C4.5076 12.5903 4.28885 12.6007 4.07357 12.5104C3.85829 12.4201 3.71593 12.2569 3.64648 12.0208C3.53537 11.7014 3.45551 11.375 3.4069 11.0417C3.35829 10.7083 3.33398 10.375 3.33398 10.0417C3.33398 8.18056 3.97982 6.59722 5.27148 5.29167C6.56315 3.98611 8.13954 3.33333 10.0007 3.33333H10.1465L9.39648 2.58333C9.24371 2.43056 9.16732 2.23611 9.16732 2C9.16732 1.76389 9.24371 1.56944 9.39648 1.41667C9.54926 1.26389 9.74371 1.1875 9.97982 1.1875C10.2159 1.1875 10.4104 1.26389 10.5632 1.41667L12.7298 3.58333C12.8965 3.75 12.9798 3.94444 12.9798 4.16667C12.9798 4.38889 12.8965 4.58333 12.7298 4.75L10.5632 6.91667C10.4104 7.06944 10.2159 7.14583 9.97982 7.14583C9.74371 7.14583 9.54926 7.06944 9.39648 6.91667C9.24371 6.76389 9.16732 6.56944 9.16732 6.33333C9.16732 6.09722 9.24371 5.90278 9.39648 5.75L10.1465 5H10.0007C8.61176 5 7.43121 5.48958 6.45898 6.46875C5.48676 7.44792 5.00065 8.63889 5.00065 10.0417ZM15.0007 9.95833C15.0007 9.73611 14.9868 9.51736 14.959 9.30208C14.9312 9.08681 14.8826 8.875 14.8132 8.66667C14.7437 8.43056 14.7507 8.20486 14.834 7.98958C14.9173 7.77431 15.0632 7.61806 15.2715 7.52083C15.4937 7.40972 15.7125 7.39931 15.9277 7.48958C16.143 7.57986 16.2854 7.74306 16.3548 7.97917C16.4659 8.29861 16.5458 8.625 16.5944 8.95833C16.643 9.29167 16.6673 9.625 16.6673 9.95833C16.6673 11.8194 16.0215 13.4028 14.7298 14.7083C13.4382 16.0139 11.8618 16.6667 10.0007 16.6667H9.85482L10.6048 17.4167C10.7576 17.5694 10.834 17.7639 10.834 18C10.834 18.2361 10.7576 18.4306 10.6048 18.5833C10.452 18.7361 10.2576 18.8125 10.0215 18.8125C9.78537 18.8125 9.59093 18.7361 9.43815 18.5833L7.27148 16.4167C7.10482 16.25 7.02148 16.0556 7.02148 15.8333C7.02148 15.6111 7.10482 15.4167 7.27148 15.25L9.43815 13.0833C9.59093 12.9306 9.78537 12.8542 10.0215 12.8542C10.2576 12.8542 10.452 12.9306 10.6048 13.0833C10.7576 13.2361 10.834 13.4306 10.834 13.6667C10.834 13.9028 10.7576 14.0972 10.6048 14.25L9.85482 15H10.0007C11.3895 15 12.5701 14.5104 13.5423 13.5313C14.5145 12.5521 15.0007 11.3611 15.0007 9.95833Z"
        fill="var(--gray40)"
      />
    </svg>
  );
};

const ReasonIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
    >
      <rect width="40" height="40" rx="20" fill="var(--blue30)" />
      <path
        d="M12 10.7264C12 10.3252 12.3252 10 12.7264 10L27.2506 10C27.6518 10 27.9771 10.3252 27.9771 10.7264L27.9771 24L21 31H12.7264C12.3252 31 12 30.6748 12 30.2736V10.7264Z"
        fill="white"
      />
      <path
        d="M21 24.5812C21 24.2602 21.2602 24 21.5812 24H27.9973L21 30.9961V24.5812Z"
        fill="var(--gray20)"
      />
    </svg>
  );
};

export default StrategyReturns;
